import {
  parseAiDeckDifficulty,
  type AiDeckDifficulty,
} from '../engine/aiDeck'
import type { Seat } from '../types/state'

/**
 * Coins paid to the winner of a ranked Quick Match. Private rooms pay 0.
 * Every finished duel pays this while `RANKED_FULL_PAY_DUELS` is 0.
 */
export const PVP_WIN_COINS = 7

/**
 * Coins paid to the loser of a finished Quick Match (not a quit).
 * A quit pays nothing.
 */
export const PVP_LOSS_COINS = 5

/** Coins paid to each seat when a Quick Match draws. Same as a finished loss. */
export const PVP_DRAW_COINS = 5

/** Casual-queue duels pay no coins. A win still grants XP. */
export const CASUAL_WIN_COINS = 0

/** Same as a finished Quick Match win. League points do not move. */
export const EVENT_QUEUE_WIN_COINS = PVP_WIN_COINS

/**
 * Same as a finished Quick Match loss. A quit pays nothing.
 */
export const EVENT_QUEUE_LOSS_COINS = PVP_LOSS_COINS

/** Same as a Quick Match draw. */
export const EVENT_QUEUE_DRAW_COINS = PVP_DRAW_COINS

/** Coins paid for beating the CPU. An Easy win pays 0. */
export const AI_NORMAL_WIN_COINS = 1
export const AI_HARD_WIN_COINS = 2
export const AI_EXPERT_WIN_COINS = 2
export const AI_MASTER_WIN_COINS = 2

/** Accounts paid per CPU ladder when a season closes. Ties at the cut are included. */
export const AI_LADDER_REWARD_TOP = 10
/** Master Packs each of those accounts gets, per ladder. */
export const AI_LADDER_REWARD_PACKS = 1

const AI_WIN_COINS: Record<AiDeckDifficulty, number> = {
  test: 0,
  easy: 0,
  normal: AI_NORMAL_WIN_COINS,
  hard: AI_HARD_WIN_COINS,
  expert: AI_EXPERT_WIN_COINS,
  master: AI_MASTER_WIN_COINS,
}

/** Coins paid for a real vs-AI loss (LP / deck out / Exodia). */
export const AI_LOSS_COINS = 1

/**
 * Finished vs-AI duels in a UTC day that can still pay coins.
 * 0 lifts the cap: every finished duel pays. Set back to 3 when
 * ranked Quick Match is open again.
 */
export const AI_COIN_DUEL_CAP = 0

/**
 * Finished ranked duels in a UTC day that still pay coins.
 * 0 lifts the cap: every finished duel pays. Later duels grant XP only
 * once this is a positive count again.
 */
export const RANKED_FULL_PAY_DUELS = 0

/** Price of one LOB or Metal Raiders pack. */
export const SHOP_PACK_PRICE = 20

/** Shop purchase sizes. One request debits price × qty. */
export const SHOP_PACK_BUY_QTYS = [1, 3, 5, 10] as const

export type ShopPackBuyQty = (typeof SHOP_PACK_BUY_QTYS)[number]

export function isShopPackBuyQty(qty: number): qty is ShopPackBuyQty {
  return (SHOP_PACK_BUY_QTYS as readonly number[]).includes(qty)
}

/**
 * Finished vs-AI duels before the next ranked-play offer.
 * Leaving mid-duel does not count. Each offer pays once, when that
 * account finishes one Quick Match.
 */
export const RANKED_NUDGE_EVERY = 3

/**
 * Coins paid for playing that Quick Match. A quit does not collect them.
 * 0 hides the offer. The ranked match itself still pays.
 */
export const RANKED_NUDGE_COINS = 0

/** Coins paid for finishing the Quick Match offered when someone is waiting. A quit does not collect them. */
export const RANKED_CALL_COINS = 1

/** Finished Quick Matches that can collect the call bonus on a UTC day. */
export const RANKED_CALL_DAILY_CAP = 20

/** Chance that clearing a card in the queue runner pays one coin. The server rolls this. */
export const QUEUE_STAR_CHANCE = 0.05

/** Coins granted when a queue-star roll hits. */
export const QUEUE_STAR_COIN = 1

/** Coins paid for reporting a bug. One paid report per calendar day. */
export const BUG_REPORT_COIN = 1

export type DuelOutcome = 'win' | 'loss'

export type CoinLedgerReason =
  | 'ai_win'
  | 'ai_loss'
  | 'pvp_win'
  | 'pvp_loss'
  | 'buy_pack'
  | 'level_up'
  | 'mission'
  | 'deploy_interrupt'
  | 'ranked_nudge'
  | 'ranked_call'
  | 'queue_star'
  | 'bug_report'
  | 'recycle'
  | 'admin_grant'
  | 'donation'
  | 'dev_grant'
  | 'dev_reset'
  | 'expansion_reset'

/**
 * True when the loser walked away (surrender / disconnect / forfeit).
 * Those seats get no consolation coins.
 */
export function isQuitReason(reason: string | null | undefined): boolean {
  return reason === 'surrender' || reason === 'disconnect' ||
    reason === 'forfeit' || reason === 'timeout'
}

/**
 * Quick Match coins for one human seat. Private rooms pay nothing.
 * The winner is always paid; a quitting loser is not.
 */
export function pvpPayoutCoins(
  seat: Seat,
  winner: Seat | null,
  winReason: string | null,
  ranked: boolean,
): number {
  if (!ranked) return 0
  if (winner === null) return PVP_DRAW_COINS
  if (seat === winner) return PVP_WIN_COINS
  return isQuitReason(winReason) ? 0 : PVP_LOSS_COINS
}

/**
 * Event-queue coins for one human seat. Same amounts as a finished Quick Match.
 * League points do not move. The winner is always paid; a quitting loser is not.
 * XP uses `pvpPayoutXp`.
 */
export function eventQueuePayoutCoins(
  seat: Seat,
  winner: Seat | null,
  winReason: string | null,
): number {
  if (winner === null) return EVENT_QUEUE_DRAW_COINS
  if (seat === winner) return EVENT_QUEUE_WIN_COINS
  return isQuitReason(winReason) ? 0 : EVENT_QUEUE_LOSS_COINS
}

/**
 * Ranked coins after `finishedBefore` duels already counted today.
 * The first `RANKED_FULL_PAY_DUELS` pay `base`. Later duels pay 0.
 * A cap of 0 lifts it: every finished duel pays `base`.
 * A quit base of 0 stays 0.
 */
export function decayedRankedCoins(base: number, finishedBefore: number): number {
  if (!Number.isFinite(base) || base <= 0) return 0
  if (RANKED_FULL_PAY_DUELS <= 0) return Math.floor(base)
  const prior = Number.isFinite(finishedBefore) ? Math.max(0, Math.floor(finishedBefore)) : 0
  if (prior >= RANKED_FULL_PAY_DUELS) return 0
  return Math.floor(base)
}

export interface AiPayoutInput {
  outcome: DuelOutcome
  difficulty: AiDeckDifficulty
  surrendered: boolean
}

/**
 * Vs-AI coins. Surrender / leave is 0. Normal and up pay for a win;
 * an Easy win pays 0. Any real loss pays.
 */
export function aiPayoutCoins(input: AiPayoutInput): number {
  if (input.surrendered) return 0
  if (input.difficulty === 'test') return 0
  if (input.outcome === 'win') return AI_WIN_COINS[input.difficulty]
  return AI_LOSS_COINS
}

/**
 * Ledger reason for a vs-AI credit. Unused when the delta is 0.
 */
export function aiLedgerReason(
  outcome: DuelOutcome,
): Extract<CoinLedgerReason, 'ai_win' | 'ai_loss'> {
  return outcome === 'win' ? 'ai_win' : 'ai_loss'
}

/**
 * Casual-queue coins for one human seat. Only a finished win pays.
 * Private rooms stay on `pvpPayoutCoins` and still pay 0.
 */
export function casualPayoutCoins(seat: Seat, winner: Seat | null): number {
  if (winner !== null && seat === winner) return CASUAL_WIN_COINS
  return 0
}

/**
 * Ledger reason for a PvP credit. Unused when the delta is 0.
 */
export function pvpLedgerReason(
  seat: Seat,
  winner: Seat | null,
): Extract<CoinLedgerReason, 'pvp_win' | 'pvp_loss'> {
  return winner === null || seat === winner ? 'pvp_win' : 'pvp_loss'
}

/**
 * Coerce a vs-AI payout body. Unknown difficulty becomes Normal.
 */
export function parseAiPayoutInput(raw: unknown): AiPayoutInput | null {
  if (!raw || typeof raw !== 'object') return null
  const body = raw as Record<string, unknown>
  if (body.kind !== 'ai') return null
  const outcome = body.outcome
  if (outcome !== 'win' && outcome !== 'loss') return null
  return {
    outcome,
    difficulty: parseAiDeckDifficulty(body.difficulty),
    surrendered: body.surrendered === true,
  }
}
